import { Button, Icon } from "@innovationnorway/lighthouse-components";
import cn from "classnames";
import React, { KeyboardEventHandler, useEffect, useRef, useState } from "react";
import { AccountTypeEnum } from "../../../main/models/account-type-enum";
import { SetFavoriteAccountsRequest } from "../../../main/models/set-favorite-accounts-request";
import { UserAccount } from "../../../main/models/user-account";
import { BREAKPOINT_ONETAB, BREAKPOINT_TWOTABS } from "../../../utils/constants";
import useWindowSize from "../../../utils/windowsSize";
import { setFavoriteAccounts } from "../../apis/set-favorite-accounts";
import { ITabs } from "../actorTabs/actorTabs";
import NotificationIcon from "../notificationIcon/notificationIcon";
import { TabsDirection } from "./enums";
import "./tabs.scss";
import TabsExpand from "./tabsExpand/tabsExpand";

export type TabProps = React.HTMLAttributes<HTMLDivElement> & {
  tabs: ITabs[];
  className?: string;
  id?: string;
  isExpanded?: boolean;
  showHiddenTabs?: boolean;
  hiddenTabs: ITabs[];
  setTabs: (tabs: ITabs[]) => void;
  setHiddenTabs: (tabs: ITabs[]) => void;
  accounts?: UserAccount[];
  setCurrentAccountPublicId: (accountPublicId: string) => void;
  reValidateTabs: () => void;
};

const Tabs = ({
  tabs,
  className,
  id,
  isExpanded = false,
  showHiddenTabs,
  hiddenTabs,
  setTabs,
  setHiddenTabs,
  accounts,
  setCurrentAccountPublicId,
  reValidateTabs,
  ...rest
}: TabProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(tabs.findIndex((tab) => tab.selected));
  const [tabsTouched, setTabsTouched] = useState(false);
  const tabListRefs = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const windowSize = useWindowSize();
  const showOneTab = windowSize.width < BREAKPOINT_ONETAB;
  const showTwoTabs = windowSize.width < BREAKPOINT_TWOTABS;

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    const endIndex = tabs.length - 1;
    const key = event.key;

    if (key === "ArrowLeft" || key === "ArrowRight" || key === "Home" || key === "End") {
      !tabsTouched && setTabsTouched(true);
    }

    switch (key) {
      case "ArrowLeft": {
        const prevIndex = activeTabIndex - 1;
        const indexToSet = prevIndex < 0 ? endIndex : prevIndex;
        setActiveTabIndex(indexToSet);
        break;
      }
      case "ArrowRight": {
        const nextIndex = activeTabIndex + 1;
        const indexToSet = nextIndex > endIndex ? 0 : nextIndex;
        setActiveTabIndex(indexToSet);
        break;
      }
      case "Home":
        setActiveTabIndex(0);
        break;
      case "End":
        setActiveTabIndex(endIndex);
        break;
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsDropdownOpen(false);
    }
  };

  const saveFavoriteTabs = async (favoriteTabs: ITabs[]) => {
    const favorites = favoriteTabs.map((t) => t.accountId);
    setFavoriteAccounts({
      favoriteAccounts: favorites
    } as SetFavoriteAccountsRequest);
  };
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOnClick = (tab: ITabs, tabIndex: number) => {
    !tabsTouched && setTabsTouched(true);
    setActiveTabIndex(tabIndex);
    setCurrentAccountPublicId(tab.accountPublicId);
  };

  const moveTab = (tab: ITabs, direction: TabsDirection) => {
    const tabIndex = hiddenTabs.findIndex((t) => t.label === tab.label);
    if (tabIndex === -1) return;

    let newHiddenTabs = [...hiddenTabs];

    if (direction === TabsDirection.Forward && tabIndex < hiddenTabs.length - 1) {
      [newHiddenTabs[tabIndex], newHiddenTabs[tabIndex + 1]] = [newHiddenTabs[tabIndex + 1], newHiddenTabs[tabIndex]];
    } else if (direction === TabsDirection.Backward && tabIndex > 0) {
      [newHiddenTabs[tabIndex], newHiddenTabs[tabIndex - 1]] = [newHiddenTabs[tabIndex - 1], newHiddenTabs[tabIndex]];
    }

    newHiddenTabs = newHiddenTabs.map((t) => ({ ...t, selected: t.label === tab.label }));

    setHiddenTabs(newHiddenTabs);

    const newTabs = newHiddenTabs.slice(0, showTwoTabs ? (showOneTab ? 1 : 2) : 3);

    saveFavoriteTabs(newTabs);
    setTabs(newTabs);
  };

  useEffect(() => {
    const selectedTab = tabs.find((tab, index) => {
      if (tab.selected) {
        setActiveTabIndex(index);
      }

      return tab.selected;
    });

    setTabsTouched(false);

    if (selectedTab) {
      setCurrentAccountPublicId(selectedTab.accountPublicId);
    }
  }, [tabs]);

  useEffect(() => {
    const focusedTabElement = tabListRefs.current?.children[activeTabIndex] as HTMLButtonElement;

    if (focusedTabElement && tabsTouched) {
      focusedTabElement.focus();
    }
  }, [activeTabIndex, tabsTouched]);

  const findNotifications = (tabs: ITabs[]) => {
    return tabs.reduce((total, tab) => {
      const account = accounts?.find((a) => a.account.publicId === tab.accountPublicId);

      if (!account) return total;

      return (
        total +
        account.applications.reduce((appTotal, app) => appTotal + app.tasks.filter((task) => task.isNew).length, 0)
      );
    }, 0);
  };

  return (
    <div className={cn("Tabs", className)} id={id} {...rest}>
      <div className="Tabs__container">
        <div
          role="tablist"
          ref={tabListRefs}
          onKeyDown={handleKeyDown}
          className={cn("Tabs__tab-group", {
            "Tabs__tab-group--expanded": isExpanded
          })}
        >
          {tabs &&
            tabs.map((t, i) => (
              <button
                onClick={() => {
                  handleOnClick(t, i);
                }}
                tabIndex={0}
                type="button"
                role="tab"
                key={`tab-${i}`}
                className={cn("data-hj-suppress Tabs__tab", {
                  "Tabs__tab--active": activeTabIndex === i,
                  "Tabs__tab--expanded": isExpanded
                })}
                aria-selected={activeTabIndex === i}
              >
                {[AccountTypeEnum.NorwegianPerson, AccountTypeEnum.UnverifiedPerson].includes(t.accountType) && (
                  <Icon prefix="fasr" iconName="user" />
                )}
                {t.label}
                <NotificationIcon notifications={findNotifications([t])} />
              </button>
            ))}
          {showHiddenTabs && hiddenTabs && (
            <div className={cn("Tabs__dropdown")} ref={dropdownRef}>
              <Button
                prefix="fass"
                icon="ellipsis"
                variant="tertiary"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                aria-expanded={isDropdownOpen}
                aria-label="Vis flere valg"
              />
              <NotificationIcon
                notifications={findNotifications(hiddenTabs.slice(showOneTab ? 1 : showTwoTabs ? 2 : 3))}
              />

              {isDropdownOpen && (
                <TabsExpand hiddenTabs={hiddenTabs} moveTab={moveTab} findNotifications={findNotifications} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tabs;
